import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import {
  StatusPill,
  Skeleton,
  Input,
  Button,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import {
  QUOTE_PHASE_LABEL,
  QUOTE_PHASE_TONE,
  quoteMoney,
  shortDate,
  type QuotePhase,
} from "./quote-format.ts";
import { shortDay } from "../date-window.ts";
import { PrebookPill } from "../prebook-pill.tsx";

type Tab = "all" | QuotePhase;
const TABS: { key: Tab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pending", label: "Pending" },
  { key: "expiring", label: "Expiring soon" },
  { key: "converted", label: "Converted" },
  { key: "expired", label: "Expired" },
];

const EMPTY_COUNTS = {
  all: 0,
  draft: 0,
  sent: 0,
  converted: 0,
  pending: 0,
  expiring: 0,
  expired: 0,
  openPaise: "0",
};

/** Columns in the table — keeps the skeleton and empty-state `colSpan` honest. */
const COL_COUNT = 7;
const PAGE_SIZE = 25;

/**
 * Store admin — Quotes list (Commerce, M4).
 *
 * The tabs are **phases**, not stored statuses: Pending / Expiring soon /
 * Expired are all `sent` rows split by the expiry window. The filter is pushed
 * down to SQL rather than applied to the page after it loads, so it composes
 * with cursor pagination instead of quietly breaking it.
 *
 * @see prototype/store/admin/all.html#quotes-list · docs/spec/23-store-quotes.md
 */
export function QuotesListPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const canManage = usePermission("store.quotes.manage");

  const list = trpc.storeQuotes.list.useQuery({
    limit: PAGE_SIZE,
    ...(tab === "all" ? {} : { phase: tab }),
    ...(q ? { q } : {}),
    ...(cursor ? { cursor } : {}),
  });
  // Tab-independent so the badges and the subtitle don't flicker on tab change.
  const counts = trpc.storeQuotes.counts.useQuery();
  const c = counts.data ?? EMPTY_COUNTS;
  const rows = list.data?.items ?? [];

  const resetPaging = () => setCursors([]);

  return (
    <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Quotes" }]}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Quotes</h1>
          {/* The deck's live stat line, computed rather than written.
              It counts *open* rather than the deck's "pending": pending and
              expiring are disjoint tabs here, so a subtitle saying "5 pending"
              beside a "Pending 3" tab would read as a contradiction. "Open" is
              also what the orders subtitle calls the same idea. */}
          <p className="mt-1.5 text-sm text-muted">
            <span className="font-mono tabular-nums text-ink">{c.pending + c.expiring}</span> open ·{" "}
            <span className="font-mono tabular-nums text-ink">{quoteMoney(c.openPaise)}</span> in
            play ·{" "}
            <span
              className={
                c.expiring > 0 ? "font-mono tabular-nums text-warn" : "font-mono tabular-nums"
              }
            >
              {c.expiring}
            </span>{" "}
            expiring this week
          </p>
        </div>
        {canManage ? (
          <Button intent="primary" size="md" asChild>
            <Link to="/store/admin/quotes/new">
              <Plus size={14} aria-hidden />
              New quote
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="mt-4 overflow-hidden rounded border border-line bg-paper shadow">
        <TabsRoot
          value={tab}
          onValueChange={(v) => {
            setTab(v as Tab);
            resetPaging();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4">
            {/* `overflow-y-hidden`: the primitive's `overflow-x-auto` also
                computes `overflow-y: auto`, and the count badges make the list
                a pixel taller than its box — enough for a stray scrollbar. */}
            <TabsList aria-label="Quote status" className="border-b-0 overflow-y-hidden">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  count={c[t.key]}
                  countTone={t.key === "expiring" ? "warn" : "default"}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 py-2">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetPaging();
                }}
                placeholder="Search quote # / buyer…"
                aria-label="Search quotes"
                className="h-8 w-full min-w-60 sm:w-72"
              />
            </div>
          </div>
          {/* Each trigger points `aria-controls` at a panel id; the table below
              isn't a Radix panel, so without these the references dangle. */}
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} forceMount className="hidden" aria-hidden />
          ))}
        </TabsRoot>

        <div className="overflow-x-auto">
          {/* 880 → 950 for the Pre-book pill in the narrowest column — same
              reason as the order book's bump. Content is unchanged. */}
          <table className="w-full min-w-[950px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-4 text-left font-medium">#</th>
                <th className="px-3 py-4 text-left font-medium">Buyer</th>
                <th className="px-3 py-4 text-left font-medium">Sent</th>
                <th className="px-3 py-4 text-left font-medium">Expires</th>
                <th className="px-3 py-4 text-right font-medium">Value</th>
                <th className="px-3 py-4 text-left font-medium">Rep</th>
                <th className="px-3 py-4 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-3 py-3" colSpan={COL_COUNT}>
                        <Skeleton className="h-6 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((quote) => (
                    <tr
                      key={quote.quoteId}
                      className="border-b border-line last:border-0 hover:bg-raised"
                    >
                      <td className="px-3 py-3">
                        <span className="flex items-center gap-2">
                          <Link
                            to="/store/admin/quotes/$quoteId"
                            params={{ quoteId: quote.quoteId }}
                            className="font-mono text-sm font-medium text-ink hover:text-sky-700"
                          >
                            {/* Never label by status — an unsent draft has no
                                number, but a numbered quote that somehow lost one
                                must not read "Draft". */}
                            {quote.docNumber || "Unnumbered"}
                          </Link>
                          {/* Not in design 18, which covers the four order/quote
                              screens but skips this one — yet the quote book is
                              where a booking actually starts, so marking orders
                              and not quotes would be the odd gap. */}
                          <PrebookPill kind={quote.kind} />
                        </span>
                      </td>
                      <td className="px-3 py-3 text-ink">{quote.customerName}</td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {shortDate(quote.sentAt)}
                      </td>
                      <td className="px-3 py-3 font-mono tabular-nums text-ink-2">
                        {/* Date-only column — UTC-pinned, see `date-window.ts`. */}
                        {shortDay(quote.expiresOn)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {quoteMoney(quote.totalPaise)}
                      </td>
                      <td className="px-3 py-3 text-muted">{quote.repName ?? "—"}</td>
                      <td className="px-3 py-3">
                        <StatusPill tone={QUOTE_PHASE_TONE[quote.phase]} size="sm" dot>
                          {QUOTE_PHASE_LABEL[quote.phase]}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
              {!list.isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-12 text-center text-sm text-muted">
                    {q
                      ? "No quotes match that search."
                      : tab === "all"
                        ? "No quotes yet. Raise one to send a buyer a priced line sheet."
                        : `No ${QUOTE_PHASE_LABEL[tab].toLowerCase()} quotes.`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Showing <span className="font-mono font-semibold text-ink">{rows.length}</span>{" "}
            {rows.length === 1 ? "quote" : "quotes"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              intent="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((p) => p.slice(0, -1))}
            >
              ← Prev
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={!list.data?.nextCursor}
              onClick={() => {
                const next = list.data?.nextCursor;
                if (next) setCursors((p) => [...p, next]);
              }}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>
    </StoreAdminLayout>
  );
}
